/**
 * Componente KPICard
 * 
 * Exibe um card de KPI (Key Performance Indicator) com label, valor, unidade e ícone.
 * 
 * @module components/Dashboard/KPISection/KPICard
 */

import React from 'react';
import PropTypes from 'prop-types';

/**
 * Componente de Card de KPI
 * 
 * Exibe um indicador de performance com:
 * - Label descritivo
 * - Valor formatado com unidade
 * - Ícone opcional
 * - Descrição contextual
 * 
 * @param {object} props - Propriedades do componente
 * @param {string} props.label - Label descritivo do KPI
 * @param {number|string} props.value - Valor do KPI (pode ser número ou string)
 * @param {string} props.unit - Unidade do valor (ex: "%", "", "R$")
 * @param {React.ComponentType} props.icon - Componente de ícone (React Icon)
 * @param {string} props.color - Classe CSS para cor do ícone (ex: "text-green-600")
 * @param {string} props.bg - Classe CSS para cor de fundo do ícone (ex: "bg-green-100")
 * @param {string} [props.className] - Classes CSS adicionais para o card
 * @param {string} [props.testId] - ID para testes (data-testid)
 * 
 * @returns {JSX.Element} Componente de card de KPI
 * 
 * @example
 * ```jsx
 * import { FaUsers } from 'react-icons/fa';
 * 
 * <KPICard
 *   label="Pacientes Ativos"
 *   value={150}
 *   unit=""
 *   icon={FaUsers}
 *   color="text-green-600"
 *   bg="bg-green-100"
 * />
 * ```
 */
const KPICard = React.memo(({
    label,
    value = 0,
    unit = '',
    icon: Icon = null,
    color = 'text-gray-600',
    bg = 'bg-gray-100',
    className = '',
    testId = null,
}) => {
    // Garantir que o valor seja exibido (0 se null/undefined)
    const displayValue = value !== undefined && value !== null ? value : 0;
    const formattedValue = `${displayValue}${unit}`;

    // Determinar descrição contextual baseada na unidade
    const description = unit === '%' 
        ? 'Métrica de Performance' 
        : 'Total na Organização';

    // Gerar ID único para acessibilidade
    const cardId = testId || `kpi-card-${label.toLowerCase().replace(/\s+/g, '-')}`;
    const valueId = `${cardId}-value`;
    const descriptionId = `${cardId}-description`;

    return (
        <div
            className={`bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 hover:-translate-y-1 flex flex-col p-6 relative z-10 hover:z-20 ${className}`}
            role="article"
            aria-labelledby={cardId}
            data-testid={testId || cardId}
        >
                {/* Header com label e ícone */}
                <div 
                    className="flex items-center justify-between mb-2"
                    role="presentation"
                >
                    <p 
                        id={cardId}
                        className="text-sm font-medium text-gray-500"
                    >
                        {label}
                    </p>
                    {Icon && (
                        <div 
                            className={`p-2 rounded-full ${bg} ${color}`}
                            role="img"
                            aria-label={`Ícone de ${label}`}
                        >
                            <Icon size={16} aria-hidden="true" />
                        </div>
                    )}
                </div>

                {/* Valor principal */}
                <h2 
                    id={valueId}
                    className="text-4xl font-extrabold text-gray-900 mt-1"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    {formattedValue}
                </h2>

                {/* Descrição contextual */}
                <p 
                    id={descriptionId}
                    className="text-xs text-gray-500 mt-1"
                    aria-label={description}
                >
                    {description}
                </p>
        </div>
    );
}, (prevProps, nextProps) => {
    // Comparação customizada para evitar re-renderizações desnecessárias
    return (
        prevProps.label === nextProps.label &&
        prevProps.value === nextProps.value &&
        prevProps.unit === nextProps.unit &&
        prevProps.icon === nextProps.icon &&
        prevProps.color === nextProps.color &&
        prevProps.bg === nextProps.bg &&
        prevProps.className === nextProps.className &&
        prevProps.testId === nextProps.testId
    );
});

KPICard.displayName = 'KPICard';

KPICard.propTypes = {
    /** Label descritivo do KPI */
    label: PropTypes.string.isRequired,
    
    /** Valor do KPI (pode ser número ou string) */
    value: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    
    /** Unidade do valor (ex: "%", "", "R$") */
    unit: PropTypes.string,
    
    /** Componente de ícone (React Icon) */
    icon: PropTypes.elementType,
    
    /** Classe CSS para cor do ícone (ex: "text-green-600") */
    color: PropTypes.string,
    
    /** Classe CSS para cor de fundo do ícone (ex: "bg-green-100") */
    bg: PropTypes.string,
    
    /** Classes CSS adicionais para o card */
    className: PropTypes.string,
    
    /** ID para testes (data-testid) */
    testId: PropTypes.string,
};

// Default props removidos - usando default parameters na função

export default KPICard;

